import React from 'react';
import useAuth from '../../hooks/useAuth';
import EmailVerificationPage from './EmailVerificationPage';
import EmailVerificationBanner from './EmailVerificationBanner';
import LandingPage from '../LandingPage/LandingPage';

interface AuthGateProps {
  children: React.ReactNode;
}

const LOGOUT_FLAG_KEY = 'auth_logged_out';

const AuthGate: React.FC<AuthGateProps> = ({ children }) => {
  const auth = useAuth();
  const { isAuthenticated, user, loading, isFirebaseAvailable, devBypassLogin } = auth;
  const isProductionBuild = process.env.NODE_ENV === 'production';

  // Check if user explicitly logged out (sessionStorage flag)
  const explicitlyLoggedOut =
    typeof sessionStorage !== 'undefined' && sessionStorage.getItem(LOGOUT_FLAG_KEY) === '1';

  // Show loading state before auth checks to avoid transient login flashes
  // while auth state is still being restored (e.g., Firebase persistence).
  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: '100vh' }}
      >
        <div className="text-center">
          <div
            className="spinner-border text-primary mb-3"
            role="status"
            aria-live="polite"
          >
            <span className="visually-hidden">Loading...</span>
          </div>
          <div className="text-muted">Checking authentication...</div>
        </div>
      </div>
    );
  }

  // Redirect to login in exactly two cases:
  // 1. User clicked Logout → isAuthenticated is false (explicitLogout or user cleared).
  // 2. No credentials (not logged in or session expired) → isAuthenticated is false.
  if (!isAuthenticated) {
    // If Firebase is unavailable but user explicitly logged out, show LandingPage
    // so they can choose to log in properly or browse as guest.
    if (!isFirebaseAvailable && !explicitlyLoggedOut) {
      return (
        <div className="container py-5">
          <div className="card p-4 mx-auto" style={{ maxWidth: 420 }}>
            <div className="alert alert-warning mb-0" role="alert">
              {isProductionBuild
                ? 'Authentication service is unavailable. Please try again later.'
                : 'Authentication service is not configured. You can continue in development mode to use the app.'}
            </div>
            {!isProductionBuild && (
              <button
                type="button"
                className="btn btn-primary w-100 mt-3"
                onClick={devBypassLogin}
              >
                Continue in development mode
              </button>
            )}
          </div>
        </div>
      );
    }
    return <LandingPage />;
  }

  // Authenticated but email not verified: show dedicated verification page
  if (user && !user.emailVerified) {
    return <EmailVerificationPage />;
  }

  return (
    <>
      <EmailVerificationBanner />
      {children}
    </>
  );
};

export default AuthGate;
